import { SowInput, SowTextarea, SowSelect } from './ValidationFields';
import type { CSSProperties } from 'react';
import { PersonFields } from './PersonFields';
import type { SowExecutivePartnerFields, SowExecutivePartnerVariant } from '../../types';

interface Props {
  fields: SowExecutivePartnerFields;
  onChange: (next: SowExecutivePartnerFields) => void;
}

const VARIANTS: Array<{ value: SowExecutivePartnerVariant; label: string }> = [
  { value: 'ep_only', label: 'Executive Partner only' },
  { value: 'hybrid_ep', label: 'Hybrid — EP copy' },
  { value: 'hybrid_coach', label: 'Hybrid — Coach copy' },
];

const area: CSSProperties = { width: '100%', height: 'auto', minHeight: 96, padding: '8px 10px', resize: 'vertical', lineHeight: 1.5 };

export function ExecutivePartnerSowForm({ fields, onChange }: Props): JSX.Element {
  const set = (patch: Partial<SowExecutivePartnerFields>) => onChange({ ...fields, ...patch });
  const isHybrid = fields.variant !== 'ep_only';
  const isCoach = fields.variant === 'hybrid_coach';

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="card">
        <div className="card-hd"><span className="d-h3">Engagement overview</span></div>
        <div className="card-bd" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label className="input-label">Variant</label>
            <SowSelect name="variant" className="select" value={fields.variant} onChange={(e) => set({ variant: e.target.value as SowExecutivePartnerVariant })}>
              {VARIANTS.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
            </SowSelect>
          </div>
          <div>
            <label className="input-label">Client company</label>
            <SowInput name="clientCompany" className="input" value={fields.clientCompany} onChange={(e) => set({ clientCompany: e.target.value })} />
          </div>
          <div>
            <label className="input-label">Ariel NetSuite Project #</label>
            <SowInput name="netsuiteProjectNumber" className="input d-mono" value={fields.netsuiteProjectNumber} onChange={(e) => set({ netsuiteProjectNumber: e.target.value })} />
          </div>
          <div>
            <label className="input-label">Engagement location</label>
            <SowInput name="engagementLocation" className="input" value={fields.engagementLocation} onChange={(e) => set({ engagementLocation: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-hd"><span className="d-h3">People</span></div>
        <div className="card-bd" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <PersonFields name="accountOwner" label="Ariel account owner" value={fields.accountOwner} onChange={(v) => set({ accountOwner: v })} showTitle={false} />
          <PersonFields name="csm" label="Ariel CSM" value={fields.csm} onChange={(v) => set({ csm: v })} showTitle={false} />
          <PersonFields name="client" label="Client" value={fields.client} onChange={(v) => set({ client: v })} />
          <PersonFields name="clientAdmin" label="Client’s admin" value={fields.clientAdmin} onChange={(v) => set({ clientAdmin: v })} showTitle={false} />
          <PersonFields name="stakeholder1" label="Key stakeholder #1" value={fields.stakeholder1} onChange={(v) => set({ stakeholder1: v })} />
          <PersonFields name="stakeholder2" label="Key stakeholder #2" value={fields.stakeholder2} onChange={(v) => set({ stakeholder2: v })} />
        </div>
      </div>

      <div className="card">
        <div className="card-hd"><span className="d-h3">Executive Partner</span></div>
        <div className="card-bd" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <PersonFields name="ep" label="Executive Partner (EP)" value={fields.ep} onChange={(v) => set({ ep: v })} showTitle={false} />
          <div style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
            <div>
              <label className="input-label">Contracted time (EP)</label>
              <SowInput name="epContractedTime" className="input" placeholder="6 months" value={fields.epContractedTime} onChange={(e) => set({ epContractedTime: e.target.value })} />
            </div>
            <div>
              <label className="input-label">Timeframe (EP)</label>
              <SowInput name="epTimeframe" className="input" value={fields.epTimeframe} onChange={(e) => set({ epTimeframe: e.target.value })} />
            </div>
          </div>
        </div>
      </div>

      {isHybrid && (
        <div className="card">
          <div className="card-hd"><span className="d-h3">Ariel coach</span></div>
          <div className="card-bd" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <PersonFields name="coach" label="Ariel coach" value={fields.coach} onChange={(v) => set({ coach: v })} showTitle={false} />
            <div style={{ display: 'grid', gap: 12, alignContent: 'start' }}>
              <div>
                <label className="input-label">Contracted time (coach)</label>
                <SowInput name="coachContractedTime" className="input" placeholder="22 hours of coaching" value={fields.coachContractedTime} onChange={(e) => set({ coachContractedTime: e.target.value })} />
              </div>
              <div>
                <label className="input-label">Timeframe (coach)</label>
                <SowInput name="coachTimeframe" className="input" value={fields.coachTimeframe} onChange={(e) => set({ coachTimeframe: e.target.value })} />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-hd">
          <span className="d-h3">Fees</span>
          <span style={{ fontSize: 12.5, color: 'var(--mute)' }}>Rendered as “{isCoach ? 'Fees to Ariel Coach' : 'Fees to EP'}”</span>
        </div>
        <div className="card-bd">
          <SowInput name="feesText" className="input d-tnum" placeholder="$60,000" value={fields.feesText} onChange={(e) => set({ feesText: e.target.value })} />
        </div>
      </div>

      <div className="card">
        <div className="card-hd"><span className="d-h3">Desired outcomes</span></div>
        <div className="card-bd">
          <SowTextarea name="desiredOutcomes" className="input" rows={4} style={area} placeholder="Paste the Desired Outcomes section copied from the parent SOW…" value={fields.desiredOutcomes} onChange={(e) => set({ desiredOutcomes: e.target.value })} />
        </div>
      </div>

      <div className="card">
        <div className="card-hd"><span className="d-h3">Project approach</span></div>
        <div className="card-bd">
          <SowTextarea name="projectApproach" className="input" rows={5} style={area} placeholder="Paste the Project Approach section copied from the parent SOW…" value={fields.projectApproach} onChange={(e) => set({ projectApproach: e.target.value })} />
        </div>
      </div>

      {isCoach && (
        <div className="card">
          <div className="card-hd"><span className="d-h3">Special instructions for the coach</span></div>
          <div className="card-bd">
            <SowTextarea name="specialInstructions" className="input" rows={3} style={area} value={fields.specialInstructions} onChange={(e) => set({ specialInstructions: e.target.value })} />
          </div>
        </div>
      )}
    </div>
  );
}
